import { Database } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardHeader, CardBody } from './Card';

export type NoDataVariant = 'neutral' | 'notice';

export interface NoDataStateProps {
  /** Primary line. Defaults to the app-wide no-data message. */
  title?: string;
  /** Optional backend detail string (e.g. the 404 `detail`). */
  message?: string;
  /** Actionable next step, shown muted below the message. */
  hint?: string;
  icon?: LucideIcon;
  /** `neutral` (default): absence of data, not an error. `notice`: amber, needs attention. */
  variant?: NoDataVariant;
  action?: { label: string; onClick: () => void; icon?: LucideIcon };
  /** Wrap in a Card. Default true. Set false to drop into an existing card body. */
  asCard?: boolean;
  /** So a chart can still identify itself when its data is missing. */
  cardTitle?: string;
  cardSubtitle?: string;
  /** Minimum body height — keeps layout from jumping on a chart→no-data swap. */
  minHeight?: number;
  className?: string;
}

// Generic fallback only. Callers should pass a `title` that names what is
// actually missing — a vague "no data" reads as wrong whenever the user can see
// related data elsewhere on the page.
const DEFAULT_TITLE = 'No data to display';

/**
 * Shared empty-state surface. One look for "there is nothing to show here yet"
 * across the financial surfaces, replacing the mix of silent `return null`,
 * ad-hoc text, and amber alerts.
 */
export default function NoDataState({
  title = DEFAULT_TITLE,
  message,
  hint,
  icon: Icon = Database,
  variant = 'neutral',
  action,
  asCard = true,
  cardTitle,
  cardSubtitle,
  minHeight = 180,
  className = '',
}: NoDataStateProps) {
  const tone =
    variant === 'notice'
      ? { iconWrap: 'bg-amber-100 text-amber-600', title: 'text-amber-900', body: 'text-amber-700' }
      : { iconWrap: 'bg-neutral-100 text-neutral-400', title: 'text-neutral-700', body: 'text-neutral-500' };

  const body = (
    <div
      className={`flex flex-col items-center justify-center text-center gap-2 px-6 py-8 ${className}`}
      style={{ minHeight }}
    >
      <div className={`h-11 w-11 rounded-full flex items-center justify-center ${tone.iconWrap}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className={`text-sm font-semibold ${tone.title}`}>{title}</p>
      {hint && <p className={`text-xs max-w-md ${tone.body}`}>{hint}</p>}
      {message && <p className="text-[11px] text-neutral-400 max-w-md">{message}</p>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
        >
          {action.icon && <action.icon className="w-3.5 h-3.5" />}
          {action.label}
        </button>
      )}
    </div>
  );

  if (!asCard) return body;

  return (
    <Card className={className}>
      {(cardTitle || cardSubtitle) && (
        <CardHeader>
          {cardTitle && <h3 className="text-sm font-semibold text-neutral-800">{cardTitle}</h3>}
          {cardSubtitle && <p className="text-xs text-neutral-500 mt-0.5">{cardSubtitle}</p>}
        </CardHeader>
      )}
      <CardBody>{body}</CardBody>
    </Card>
  );
}
